import { useDebouncedValue } from '@mantine/hooks';
import { Spotlight, SpotlightActionGroupData } from '@mantine/spotlight';
import {
  IconArrowRight,
  IconBolt,
  IconBuilding,
  IconHome,
  IconInbox,
  IconPlus,
  IconSearch,
  IconSettings,
  IconUsers
} from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Avatar, Loader, Text } from '@mantine/core';
import { useViews } from '../../hooks/QueryHooks';
import { getGlobalSearch } from '../../services/globalSearchEndpoint';
import useWorkspaceStore from '../../store/workspace/workspaceStore';

/**
 * Enhanced GlobalSpotlight with improved design, icons, and functionality
 */
export default function GlobalSpotlight() {
  const params = useParams();
  const navigate = useNavigate();
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);

  const { data: views }: any = useViews({ workspaceId, enabled: !!workspaceId });

  // Keep default views for accounts & users
  const defaultView = useMemo(() => {
    if (!Array.isArray(views)) return { accounts: null, users: null } as any;
    return views
      .filter((v: any) => v?.is_precreated)
      .reduce((acc: any, v: any) => {
        if (v?.entity_relation) acc[v.entity_relation] = v;
        return acc;
      }, {} as any);
  }, [views]);

  const [query, setQuery] = useState('');
  const [debounced] = useDebouncedValue(query, 400);
  const [actions, setActions] = useState<SpotlightActionGroupData[]>([]);
  const [loading, setLoading] = useState(false);

  // Quick actions when no search query
  const getQuickActions = (): SpotlightActionGroupData[] => [
    {
      group: 'Navigation',
      actions: [
        {
          id: 'hq',
          highlightColor: "gray",
          label: 'Go to Headquarters',
          description: 'Your central command center',
          leftSection: <IconHome size={16} color="#6366f1" />,
          rightSection: <Text size="xs" c="dimmed">⌘H</Text>,
          onClick: () => navigate(`/home/${params.workspaceId}/headquarters`),
        },
        {
          id: 'inbox',
          highlightColor: "gray",
          label: 'Go to Inbox',
          description: 'Check your signal notifications',
          leftSection: <IconInbox size={16} color="#f59e0b" />,
          rightSection: <Text size="xs" c="dimmed">⌘I</Text>,
          onClick: () => navigate(`/home/${params.workspaceId}/inbox`),
        },
        {
          id: 'signals',
          label: 'Signal Automations',
          highlightColor: "gray",
          description: 'Manage your automated workflows',
          leftSection: <IconBolt size={16} color="#8b5cf6" />,
          rightSection: <Text size="xs" c="dimmed">⌘S</Text>,
          onClick: () => navigate(`/home/${params.workspaceId}/signals`),
        },
        {
          id: 'accounts-view',
          label: 'All Accounts',
          highlightColor: "gray",
          description: 'View and manage your accounts',
          leftSection: <IconBuilding size={16} color="#10b981" />,
          rightSection: <IconArrowRight size={14} color="gray" />,
          onClick: () => {
            const viewId = defaultView?.accounts?.id;
            if (viewId) {
              navigate(`/home/${params.workspaceId}/view/${viewId}`);
            }
          },
        },
        {
          id: 'contacts-view',
          highlightColor: "gray",
          label: 'All Contacts',
          description: 'View and manage your contacts',
          leftSection: <IconUsers size={16} color="#3b82f6" />,
          rightSection: <IconArrowRight size={14} color="gray" />,
          onClick: () => {
            const viewId = defaultView?.users?.id;
            if (viewId) {
              navigate(`/home/${params.workspaceId}/view/${viewId}`);
            }
          },
        }
      ],
    },
    {
      group: 'Actions',
      actions: [
        {
          id: 'create-signal',
          highlightColor: "gray",
          label: 'Create New Signal',
          description: 'Set up a new automated workflow',
          leftSection: <IconPlus size={16} color="#8b5cf6" />,
          onClick: () => navigate(`/home/${params.workspaceId}/signals/new`),
        },
        {
          id: 'integrations',
          highlightColor: "gray",
          label: 'Integration Settings',
          description: 'Configure your workspace preferences',
          leftSection: <IconSettings size={16} color="#6b7280" />,
          onClick: () => navigate(`/home/${params.workspaceId}/settings/integrations`),
        },
      ],
    },
  ];

  // Loader shown while fetching data
  const loaderActions = useMemo<SpotlightActionGroupData[]>(
    () => [
      {
        group: 'Searching...',
        actions: [
          {
            id: 'loading',
            highlightColor: "gray",
            label: 'Finding results...',
            description: 'Please wait while we search',
            leftSection: <Loader size="xs" color="blue" />,
            onClick: () => { },
            closeSpotlightonClick: false,
          },
        ],
      },
    ],
    [],
  );

  // Convert API payload to enhanced SpotlightActionGroupData[]
  const transformResults = (results: Record<string, any[]>): any =>
    Object.entries(results || {})?.map(([category, items]) => ({
      group: `${category.charAt(0).toUpperCase()}${category.slice(1)}`,
      actions: items.map((item) => ({
        id: `${category}-${item.external_id ?? item.id}`,
        highlightColor: "gray",
        label: item.identifier || item.view_name || item.name || 'Unknown',
        description: category === 'accounts' && item.domain ? `${category} • ${item.domain}${item.mrr ? ` • $${item.mrr} MRR` : ''}` : category,
        leftSection: (
          <Avatar
            size={32}
            variant="filled"
            color="initials"
            name={item?.identifier || item?.name || item?.view_name}
            src={item?.avatar_url}
            radius="sm"
          />
        ),
        rightSection: <IconArrowRight size={14} color="gray" />,
        onClick: () => {
          if (!params.workspaceId) return;
          if (category === 'accounts') {
            const viewId = defaultView?.accounts?.id;
            if (viewId) {
              navigate(`/home/${params.workspaceId}/view/${viewId}/account/${item.external_id}/overview`);
            }
          } else if (category === 'contacts') {
            const viewId = defaultView?.users?.id;
            if (viewId) {
              navigate(`/home/${params.workspaceId}/view/${viewId}/user/${item.external_id}`);
            }
          } else if (category === 'views') {
            navigate(`/home/${params.workspaceId}/view/${item.external_id}`);
          }
          setQuery('');
        },
        keywords: [item.identifier, item.name, item.view_name, item.domain].filter(Boolean),
      })),
    }));

  // Fetch results whenever debounced query changes
  useEffect(() => {
    const fetchData = async () => {
      if (!workspaceId) {
        setActions([]);
        setLoading(false);
        return;
      }

      // Show quick actions if no search query
      if (debounced.length < 2) {
        setActions(getQuickActions());
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await getGlobalSearch({
          search_query: debounced,
          workspace_identifier: workspaceId,
          limit: 15,
        });
        const results = response?.data?.results || {};
        const transformedResults = transformResults(results);

        // If no search results, show "no results" action
        if (transformedResults.length === 0) {
          setActions([
            {
              group: 'No Results',
              actions: [
                {
                  id: 'no-results',
                  highlightColor: "gray",
                  label: `No results found for "${debounced}"`,
                  description: 'Try a different search term',
                  leftSection: <IconSearch size={16} color="gray" />,
                  onClick: () => { },
                },
              ],
            },
          ]);
        } else {
          setActions(transformedResults);
        }
      } catch (err) {
        console.error('Global search error', err);
        setActions([
          {
            group: 'Error',
            actions: [
              {
                id: 'error',
                highlightColor: "gray",
                label: 'Search failed',
                description: 'Please try again later',
                leftSection: <IconSearch size={16} color="red" />,
                onClick: () => { },
              },
            ],
          },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [debounced, workspaceId, params.workspaceId, defaultView]);

  return (
    <Spotlight
      actions={loading ? loaderActions : actions}
      nothingFound="Start typing to search..."
      color="gray"
      limit={20}
      radius="md"
      size="lg"
      styles={{
        action: {
          '&:hover': { backgroundColor: '#f3f4f6 !important' },
          '&:focus': { backgroundColor: '#f3f4f6 !important' },
          '&:active': { backgroundColor: '#f3f4f6 !important' },
          '&[data-selected]': {
            backgroundColor: '#f3f4f6 !important',
            color: 'black !important',
          },
        },
        root: {
          '--mantine-primary-color-contrast': 'black',
          '--mantine-primary-color-filled': '#f3f4f6',
        },
      }}
      searchProps={{
        leftSection: <IconSearch size={20} stroke={1.5} color="#6b7280" />,
        placeholder: 'Search accounts, contacts, views... or try ⌘K',
        value: query,
        onChange: (event) => setQuery(event.currentTarget.value),
        size: "md",
        radius: "md",
        styles: {
          input: {
            color: 'black',
            fontSize: '16px',
            fontWeight: 500,
          },
        },
      }}
      overlayProps={{
        backgroundOpacity: 0.4,
        blur: 4,
      }}
      shortcut={null}
    />
  );
}
